/**
 * Runtime watchdog for the floating WhatsApp CTA.
 *
 * Verifies on an interval and on every DOM mutation that the CTA:
 *   1. Exists in the DOM
 *   2. Is `position: fixed`
 *   3. Is anchored to the bottom-right of the viewport
 *   4. Has z-index >= 50
 *   5. Is visible (non-zero size, not display:none / visibility:hidden)
 *
 * On violation it logs a structured `console.error` and dispatches a
 * `whatsapp-cta:violation` CustomEvent so external monitoring (Sentry,
 * GA, etc.) can subscribe without coupling to this module.
 *
 * Throttled to one report per violation kind every 10s to avoid spam.
 */

const SELECTOR = 'a[aria-label="Chat on WhatsApp"]';
const CHECK_INTERVAL_MS = 5_000;
const REPORT_THROTTLE_MS = 10_000;
/** Grace window before a not-yet-mounted CTA is treated as missing. */
const MOUNT_GRACE_MS = 8_000;

type ViolationKind =
  | "missing"
  | "not-fixed"
  | "low-z-index"
  | "not-bottom-right"
  | "hidden"
  | "too-small";

interface Violation {
  kind: ViolationKind;
  detail: Record<string, unknown>;
  url: string;
  ts: number;
}

const lastReportAt = new Map<ViolationKind, number>();

function report(violation: Violation) {
  const last = lastReportAt.get(violation.kind) ?? 0;
  if (Date.now() - last < REPORT_THROTTLE_MS) return;
  lastReportAt.set(violation.kind, Date.now());

  // eslint-disable-next-line no-console
  console.error("[whatsapp-cta watchdog]", violation);

  try {
    window.dispatchEvent(
      new CustomEvent("whatsapp-cta:violation", { detail: violation }),
    );
  } catch {
    /* no-op */
  }
}

let startedAt = 0;
let everSeen = false;

function check(): void {
  if (typeof document === "undefined") return;
  const el = document.querySelector<HTMLElement>(SELECTOR);
  const url = window.location.pathname + window.location.search;
  const ts = Date.now();

  if (!el) {
    // The CTA mounts after React hydration. Only report "missing" once it has
    // either been seen before, or the mount grace window has elapsed.
    if (everSeen || ts - startedAt > MOUNT_GRACE_MS) {
      report({ kind: "missing", detail: { selector: SELECTOR }, url, ts });
    }
    return;
  }
  everSeen = true;


  const cs = window.getComputedStyle(el);
  const rect = el.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  if (cs.display === "none" || cs.visibility === "hidden" || Number(cs.opacity) === 0) {
    report({
      kind: "hidden",
      detail: { display: cs.display, visibility: cs.visibility, opacity: cs.opacity },
      url,
      ts,
    });
    return;
  }

  if (cs.position !== "fixed") {
    report({ kind: "not-fixed", detail: { position: cs.position }, url, ts });
  }

  const zi = Number(cs.zIndex);
  if (!Number.isFinite(zi) || zi < 50) {
    report({ kind: "low-z-index", detail: { zIndex: cs.zIndex }, url, ts });
  }

  if (rect.width < 44 || rect.height < 44) {
    report({
      kind: "too-small",
      detail: { width: rect.width, height: rect.height },
      url,
      ts,
    });
  }

  // Bottom-right anchored: center must be in the right & bottom quadrant
  // and fully inside the viewport.
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const insideViewport =
    rect.left >= 0 && rect.top >= 0 && rect.right <= vw && rect.bottom <= vh;
  const bottomRight = cx > vw / 2 && cy > vh / 2;
  if (!insideViewport || !bottomRight) {
    report({
      kind: "not-bottom-right",
      detail: {
        rect: { x: rect.left, y: rect.top, w: rect.width, h: rect.height },
        viewport: { w: vw, h: vh },
        insideViewport,
        bottomRight,
      },
      url,
      ts,
    });
  }
}

let started = false;
let intervalId: number | undefined;
let observer: MutationObserver | undefined;
let rafId: number | undefined;

function scheduleCheck() {
  if (rafId !== undefined) return;
  rafId = window.requestAnimationFrame(() => {
    rafId = undefined;
    check();
  });
}

export function startWhatsAppCtaWatchdog(): void {
  if (started || typeof window === "undefined") return;
  started = true;
  startedAt = Date.now();
  everSeen = false;

  // Initial check after first paint.
  scheduleCheck();

  // Periodic check catches style drift (e.g. CSS overrides applied later).
  intervalId = window.setInterval(check, CHECK_INTERVAL_MS);

  // Mutation-driven check catches DOM removal or attribute changes.
  observer = new MutationObserver(() => scheduleCheck());
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["class", "style", "hidden"],
  });

  window.addEventListener("resize", scheduleCheck);
  window.addEventListener("orientationchange", scheduleCheck);
}

export function stopWhatsAppCtaWatchdog(): void {
  if (!started) return;
  started = false;
  if (intervalId !== undefined) window.clearInterval(intervalId);
  if (observer) observer.disconnect();
  if (rafId !== undefined) window.cancelAnimationFrame(rafId);
  window.removeEventListener("resize", scheduleCheck);
  window.removeEventListener("orientationchange", scheduleCheck);
}
